import {
    Icon,
    IconButton,
    Popover,
    PopoverArrow,
    PopoverContent,
    PopoverTrigger,
    useDisclosure,
} from '@chakra-ui/react';
import { Column, Table } from '@tanstack/react-table';
import { PropsWithChildren, useRef } from 'react';
import { BsFunnel, BsFunnelFill } from 'react-icons/bs';

import { FilterType } from '../../_openapi/portfolio-optimizer';
import { BooleanFilter } from '../filters/BooleanFilter';
import { DateFilter } from '../filters/DateFilter';
import { parseFilterStringToObject } from '../filters/filterUtils';
import { MultiSelectFilter } from '../filters/MultiSelectFilter';
import { NumberFilter, NumberFilterValue } from '../filters/NumberFilter';
import { TextFilter } from '../filters/TextFilter';

export const ColumnFilter = <T,>({ column, table }: { column: Column<T, unknown>; table: Table<T> }) => {
    const initialFocusRef = useRef<HTMLInputElement | null>(null);
    const filterValue = column.getFilterValue();

    switch (column.columnDef.meta?.filterType) {
        case FilterType.LIST:
            return (
                <FilterWrapper column={column}>
                    <MultiSelectFilter
                        allowClear
                        options={column.columnDef.meta?.filterOptionsFn?.(table) as string[]}
                        filterValue={filterValue as string[] | undefined}
                        setFilterValue={column.setFilterValue}
                    />
                </FilterWrapper>
            );
        case FilterType.DATE: {
            return (
                <FilterWrapper column={column}>
                    <DateFilter
                        filterValue={filterValue as string | undefined}
                        setFilterValue={column.setFilterValue}
                    />
                </FilterWrapper>
            );
        }

        case FilterType.NUMBER: {
            const parsedValue = filterValue
                ? (parseFilterStringToObject(filterValue as string) as NumberFilterValue)
                : undefined;
            return (
                <FilterWrapper column={column}>
                    <NumberFilter
                        filterValue={parsedValue}
                        setFilterValue={(filterVal) =>
                            column.setFilterValue(filterVal ? encodeURIComponent(JSON.stringify(filterVal)) : undefined)
                        }
                    />
                </FilterWrapper>
            );
        }
        case FilterType.BOOLEAN:
            return (
                <FilterWrapper column={column}>
                    <BooleanFilter
                        allowClear
                        filterValue={filterValue as boolean | undefined}
                        setFilterValue={column.setFilterValue}
                    />
                </FilterWrapper>
            );
        default:
            return (
                <FilterWrapper column={column} initialFocusRef={initialFocusRef}>
                    <TextFilter
                        filterValue={filterValue as string[] | undefined}
                        setFilterValue={column.setFilterValue}
                        inputRef={initialFocusRef}
                    />
                </FilterWrapper>
            );
    }
};

const FilterWrapper = <T,>({
    column,
    initialFocusRef,
    children,
}: PropsWithChildren<{
    column: Column<T, unknown>;
    initialFocusRef?: React.MutableRefObject<HTMLInputElement | null>;
}>) => {
    const { onOpen, onClose, isOpen } = useDisclosure();

    return (
        <Popover
            isLazy
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            placement="bottom"
            closeOnBlur={true}
            initialFocusRef={initialFocusRef}
        >
            <PopoverTrigger>
                <IconButton
                    aria-label=""
                    icon={
                        <Icon
                            color={column.getIsFiltered() ? 'teal.200' : 'gray.400'}
                            as={column.getIsFiltered() ? BsFunnelFill : BsFunnel}
                        />
                    }
                    size="xs"
                    variant="ghost"
                />
            </PopoverTrigger>
            <PopoverContent p={4} boxShadow="dark-lg">
                <PopoverArrow />

                {children}
            </PopoverContent>
        </Popover>
    );
};
